import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Send } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatusOption = ({ id, label, count, checked, disabled, onChange }) => (
  <label className={`flex items-center gap-3 rounded-lg border p-3 cursor-pointer ${checked ? 'border-emerald-300 bg-emerald-50/50' : 'hover:bg-slate-50'} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`} data-testid={`kvkk-publish-status-${id}`}>
    <Checkbox checked={checked} disabled={disabled} onCheckedChange={(c) => onChange(!!c)} />
    <span className="flex-1 text-sm font-medium text-slate-800">{label}</span>
    {count !== undefined && <span className="text-xs text-slate-500">{count} firma</span>}
  </label>
);

export const KvkkPublishDialog = ({ path = 'kvkk-template', version, onClose, onPublished }) => {
  const [statuses, setStatuses] = useState([]);
  const [all, setAll] = useState(false);
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!version) return;
    setAll(false); setSelected([]);
    axios.get(`${API}/company-statuses`).then(res => setStatuses(res.data)).catch(() => setStatuses([]));
  }, [version]);

  const toggle = (id, on) => setSelected(s => (on ? [...s, id] : s.filter(x => x !== id)));

  const publish = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${API}/settings/${path}/${version.id}/publish`, { target_all: all, target_status_ids: all ? [] : selected });
      toast.success(`v${res.data.version} yayınlandı. ${res.data.requested_count} firmadan yeni taahhütname istendi ve e-posta gönderildi.`);
      onPublished();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Yayınlanamadı');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!version} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="kvkk-publish-dialog">
        <DialogHeader>
          <DialogTitle>v{version?.version} Taahhütnamesini Yayınla</DialogTitle>
          <DialogDescription>Hangi firma statülerindeki firmalardan imzalı yeni taahhütname istensin? Seçilen firmalara e-posta gider ve yeni belgeyi yükleyene kadar çalışma izni talebi oluşturamazlar.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <StatusOption id="all" label="Tüm firmalar" checked={all} onChange={setAll} />
          {statuses.map(s => (
            <StatusOption key={s.id} id={s.id} label={s.name} count={s.company_count} disabled={all} checked={all || selected.includes(s.id)} onChange={(on) => toggle(s.id, on)} />
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="kvkk-publish-cancel">Vazgeç</Button>
          <Button onClick={publish} disabled={saving || (!all && !selected.length)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="kvkk-publish-confirm">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}Yayınla
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
